/**
 * useFormPopulation
 *
 * Owns form-DOM update/populate logic and radio-group visibility management:
 *
 *   colorMapRef       — field background-color map; set once per return_init via setColorMap()
 *   setColorMap       — called by the socket message handler on return_init / return_prompt_prioryear
 *   UpdateForm        — patch DOM fields from a calc-server update_form response
 *   PopulateForm      — fully populate DOM fields from a form_load response
 *   InspectFormData   — side-effect: push special field values (name, filing status, cltr) to Redux
 *   toggleGroup       — radio-group "X" toggle handler; registered as navCallbacksRef.onToggleGroup
 *   setGroupVisibility — show/hide a group's CSS members
 *   handleGroupFields  — dispatch setGroupVisibility based on value
 *   hideGroups         — hide a colon-separated list of groups
 *   initializeGroups   — guarantee every radio group has a checked default on initial form load
 */
import { useRef, useCallback } from 'react';
import $ from 'jquery';
import { collapseAssetForceGroups } from './collapseAssetForceGroups';
import { applyDisabledButtons } from './formButtonState';
import { translateSpecialOccuranceRow } from '~/app/Utility/general.js';
import { store } from '~/app/redux/index';
import { actions as returnProfileActions } from '~/app/redux/returnProfile/duck';

// Matches table-style field IDs (e.g. "AB12") — used to track maxTableRow
const tableRegExp = /^[A-Za-z]{2}[A-Za-z0-9`@]{2}/;

const useFormPopulation = ({
  isBusiness,
  fieldEventsRef, // { current: { updateFieldValueCache, maxTableRowRef, formatIDForJquery } }
  onUpdatePrintCltr, // (value: string) => void — called when the CLTR00 field changes
  onShowErrorSnackbar, // (msg: string) => void
}) => {
  // Filled by setColorMap() on return_init.
  const colorMapRef = useRef({});

  // Tracks name/filing-status to avoid redundant Redux dispatches.
  const clientDataRef = useRef({
    first_name: '',
    last_name: '',
    filing_status: undefined,
  });

  const setColorMap = useCallback(map => {
    colorMapRef.current = map;
  }, []);

  // ─── Group visibility ──────────────────────────────────────────────────────

  const setGroupVisibility = useCallback((groupID, isVisible) => {
    const grpNum = groupID.substring(3, 4); // grp<num><occurrence>
    const members = $('.form-group-' + grpNum);
    if (isVisible) {
      members.removeClass('hide');
      members.closest('.form-textitem').removeClass('hide');
    } else {
      members.addClass('hide');
      members.closest('.form-textitem').addClass('hide');
    }
    collapseAssetForceGroups();
  }, []);

  const handleGroupFields = useCallback(
    (field, value) => {
      if (value === 'X') {
        setGroupVisibility(field, true);
      } else {
        setGroupVisibility(field, false);
      }
    },
    [setGroupVisibility],
  );

  const hideGroups = useCallback(
    argString => {
      const args = argString.split(':');
      for (let i = 0; i < args.length; i++) {
        setGroupVisibility(args[i], false);
      }
    },
    [setGroupVisibility],
  );

  // Guarantee all groups have a checked default.
  // DefaultGroupState is a map: [groupID] -> { checked: bool, args: string (colon-separated IDs) }
  const initializeGroups = useCallback(
    defaultGroupState => {
      const excluded = [];
      for (const key in defaultGroupState) {
        if (excluded.includes(key)) continue;
        const value = defaultGroupState[key];
        if (!value.args.includes(':')) {
          const args = value.args.split(':');
          if (value.checked) {
            excluded.push(key);
            args.forEach(a => excluded.push(a));
            continue;
          } else {
            let isArgChecked = false;
            for (const argIdx in args) {
              const jqueryID = fieldEventsRef.current?.formatIDForJquery(args[argIdx]);
              const argField = $('#' + jqueryID);
              if (argField.length && argField[0].checked) {
                isArgChecked = true;
                break;
              }
            }
            if (isArgChecked) {
              excluded.push(key);
              args.forEach(a => excluded.push(a));
            } else {
              const field = $('#' + key)[0];
              field.click();
              args.forEach(a => excluded.push(a));
            }
          }
        } else {
          const field = $('#' + key)[0];
          field.click();
        }
      }
    },
    [fieldEventsRef],
  );

  // Called by useFormNavigation via navCallbacksRef.onToggleGroup
  const toggleGroup = useCallback(
    (e, toggleGroupNumber, event) => {
      if (event.target.value !== 'x' && event.target.value !== 'X' && event.target.value !== '') {
        const grp = document.getElementById(event.target.id);
        grp.value = '';
        onShowErrorSnackbar?.('Invalid Keypress. Enter X or Blank Only.');
        return;
      }
      if (e) {
        const args = e.getAttribute('args').split(':');
        for (let i = 0; i < args.length; i++) {
          setGroupVisibility(args[i], false);
        }
        const members = $('.form-group-' + toggleGroupNumber);
        if (event.target.value === 'X') {
          members.addClass('hide');
          setGroupVisibility(args[0], true);
        } else {
          members.removeClass('hide');
        }
      }
    },
    [setGroupVisibility, onShowErrorSnackbar],
  );

  // ─── InspectFormData ────────────────────────────────────────────────────────

  const InspectFormData = useCallback(
    (field, value) => {
      const clientData = clientDataRef.current;
      switch (field) {
        case 'FLST00':
          if ((value || value === '') && value !== clientData.filing_status) {
            clientData.filing_status = value;
            store.dispatch(
              returnProfileActions.setClientData({ filing_status: clientData.filing_status }),
            );
          }
          break;
        case 'BKAD00': // business name 1120/1120S/990
        case 'NMCD00': // business name 1041
          if (value !== clientData.name) {
            clientData.name = value;
            store.dispatch(returnProfileActions.setClientData({ name: value }));
          }
          break;
        case 'PNMA00':
          if (isBusiness) {
            if (value !== clientData.name) {
              clientData.name = value;
              store.dispatch(returnProfileActions.setClientData({ name: value }));
            }
          } else if (value !== clientData.last_name) {
            clientData.first_name = value;
            store.dispatch(
              returnProfileActions.setClientData({
                first_name: value,
                last_name: clientData.last_name,
              }),
            );
          }
          break;
        case 'PNMC00':
          if (value !== clientData.last_name) {
            clientData.last_name = value;
            store.dispatch(
              returnProfileActions.setClientData({
                first_name: clientData.first_name,
                last_name: value,
              }),
            );
          }
          break;
        case 'CLTR00':
          onUpdatePrintCltr?.(value);
          break;
        default:
          break;
      }
    },
    [isBusiness, onUpdatePrintCltr],
  );

  // ─── UpdateForm ─────────────────────────────────────────────────────────────
  // Called from the socket message handler for update_form / return_msgresp responses.

  const UpdateForm = useCallback(
    data => {
      applyDisabledButtons(data.disabledButtons);
      if (!data.fields) return;
      for (const field in data.fields) {
        const value = data.fields[field].value;
        let nxtFld = data.fields[field].nextFld;
        if (typeof nxtFld === 'undefined') nxtFld = '';

        fieldEventsRef.current?.updateFieldValueCache(field, value, nxtFld);
        InspectFormData(field, value);

        const element = document.getElementById(field);
        if (element) {
          element.value = value;
          element.style.setProperty('--background', colorMapRef.current[data.fields[field].color]);

          const containsButtonIconClass = element.classList.contains('form-button-icon');
          if (containsButtonIconClass) {
            const svgid = element.getAttribute('svgid');
            element.querySelector('svg').setAttribute('width', '100%');
            const svgText = element.querySelector('#' + svgid + ' text');
            if (svgText) svgText.setAttribute('font-size', '11');
            element.value === 'X'
              ? element.classList.add('form-button-icon-active')
              : element.classList.remove('form-button-icon-active');
            const containsActiveClass = element.classList.contains('form-button-icon-active');
            const svgIDg = document.querySelector('#' + svgid + ' g');
            // CLO-3071 — special exclusion for collapse icon buttons
            if (svgIDg && svgid !== 'ivmcollap0' && svgid !== 'ivmcollap1') {
              if (containsActiveClass) {
                svgIDg.setAttribute('fill', '#d7ecf7'); // selected
              } else {
                svgIDg.setAttribute('fill', '#FEFEFE'); // default
              }
            }
          }

          if (field.includes('grp')) {
            handleGroupFields(field, value);
          }

          if (tableRegExp.test(field)) {
            const curTableRow = translateSpecialOccuranceRow(field.substring(2, 4));
            if (
              fieldEventsRef.current?.maxTableRowRef?.current !== undefined &&
              curTableRow > fieldEventsRef.current.maxTableRowRef.current
            ) {
              fieldEventsRef.current.maxTableRowRef.current = curTableRow;
            }
          }
        } else if (field.includes('grp')) {
          handleGroupFields(field, value);
        }
      }
    },
    [InspectFormData, handleGroupFields, fieldEventsRef],
  );

  // ─── PopulateForm ──────────────────────────────────────────────────────────
  // Called from the socket message handler for form_load responses.

  const PopulateForm = useCallback(
    async data => {
      const flds = data.fields;
      const defaultGroupState = {};

      for (const elementID in flds) {
        const aVAL = flds[elementID].value;
        let nxtFld = flds[elementID].nextFld;
        const restricted = flds[elementID]?.restricted;
        if (typeof nxtFld === 'undefined') nxtFld = '';

        InspectFormData(elementID, aVAL);
        // NOTE: elementID MUST be string-quoted to successfully select IDs with dollar signs
        const element = $(`[id="${elementID}"]`)[0];
        if (element) {
          element.style.setProperty('--background', colorMapRef.current[flds[elementID].color]);
          fieldEventsRef.current?.updateFieldValueCache(elementID, aVAL, nxtFld);

          if (restricted) {
            element.classList.add('restricted');
            element.setAttribute('readonly', '');
            element.setAttribute('restricted', true);
          } else {
            element.setAttribute('restricted', false);
          }

          if (element.classList.contains('form-checkbox-input') || element?.type === 'radio') {
            element.value = aVAL === 'X' ? 'X' : '';
            if (element.id.includes('grp')) {
              if (element.value === 'X') {
                setGroupVisibility(element.id, true);
              }
              defaultGroupState[element.id] = {
                checked: element.value === 'X',
                args: element.getAttribute('args') ?? '',
              };
            }
          }

          element.value = aVAL;

          if (tableRegExp.test(elementID)) {
            const curTableRow = translateSpecialOccuranceRow(elementID.substring(2, 4));
            if (
              fieldEventsRef.current?.maxTableRowRef?.current !== undefined &&
              curTableRow > fieldEventsRef.current.maxTableRowRef.current
            ) {
              fieldEventsRef.current.maxTableRowRef.current = curTableRow;
            }
          }
        } else if (elementID.includes('grp')) {
          handleGroupFields(elementID, aVAL);
        }
      }

      initializeGroups(defaultGroupState);
      collapseAssetForceGroups();
      applyDisabledButtons(data.disabledButtons);
    },
    [InspectFormData, setGroupVisibility, handleGroupFields, initializeGroups, fieldEventsRef],
  );

  return {
    colorMapRef,
    setColorMap,
    UpdateForm,
    PopulateForm,
    InspectFormData,
    toggleGroup,
    setGroupVisibility,
    handleGroupFields,
    hideGroups,
    initializeGroups,
  };
};

export default useFormPopulation;
